/**
 * Unified AI Provider Configuration
 *
 * Supports multiple AI providers through Vercel AI SDK:
 * - OpenAI (native)
 * - Anthropic Claude (native)
 * - Google Gemini (native)
 * - MiniMax (Anthropic-compatible, recommended by official)
 * - OpenAI-compatible providers (DeepSeek, Qwen, Kimi, GLM, SiliconFlow, Doubao, Tencent, Xiaomi, Lemonade, etc.)
 *
 * Sources:
 * - https://platform.openai.com/docs/models
 * - https://platform.claude.com/docs/en/about-claude/models/overview
 * - https://ai.google.dev/gemini-api/docs/models
 * - https://api-docs.deepseek.com/quick_start/pricing
 * - https://platform.moonshot.cn/docs/pricing/chat
 * - https://platform.minimaxi.com/docs/guides/text-generation
 * - https://platform.minimaxi.com/docs/api-reference/text-anthropic-api
 * - https://docs.bigmodel.cn/cn/guide/start/model-overview
 * - https://help.aliyun.com/zh/model-studio/models (Qwen/DashScope)
 * - https://siliconflow.cn/models
 * - https://siliconflow.cn/pricing
 * - https://www.volcengine.com/docs/82379/1330310
 */

import { createOpenAI } from '@ai-sdk/openai';
import { createAnthropic } from '@ai-sdk/anthropic';
import { createGoogleGenerativeAI } from '@ai-sdk/google';
import type { LanguageModel } from 'ai';
import type {
  ProviderId,
  ProviderConfig,
  ModelInfo,
  ModelConfig,
  ThinkingConfig,
} from '@/lib/types/provider';
import { applyModelMetadata, getCatalogThinkingCapability } from './model-metadata';
import { getThinkingMode, pickThinkingBudget } from './thinking-config';
import { createLogger } from '@/lib/logger';
// NOTE: Do NOT import thinking-context.ts here — it uses node:async_hooks
// which is server-only, and this file is also used on the client via
// settings.ts. The thinking context is read from globalThis instead
// (set by thinking-context.ts at module load time on the server).

const log = createLogger('AIProviders');

// Re-export types for backward compatibility
export type { ProviderId, ProviderConfig, ModelInfo, ModelConfig };

/** Provider IDs whose logos are monochrome-dark and need `dark:invert` in dark mode */
export const MONO_LOGO_PROVIDERS: ReadonlySet<string> = new Set(['openai', 'openrouter', 'ollama']);

/**
 * Provider registry
 */
export const PROVIDERS: Record<ProviderId, ProviderConfig> = {
  openai: {
    id: 'openai',
    name: 'OpenAI',
    type: 'openai',
    defaultBaseUrl: 'https://api.openai.com/v1',
    requiresApiKey: true,
    icon: '/logos/openai.svg',
    models: [
      {
        id: 'gpt-5.5',
        name: 'GPT-5.5',
        contextWindow: 1050000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'gpt-5.4-pro',
        name: 'GPT-5.4 Pro',
        contextWindow: 1050000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'gpt-5.4',
        name: 'GPT-5.4',
        contextWindow: 1050000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'gpt-5.4-mini',
        name: 'GPT-5.4 Mini',
        contextWindow: 400000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'gpt-5.4-nano',
        name: 'GPT-5.4 Nano',
        contextWindow: 400000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
    ],
  },

  anthropic: {
    id: 'anthropic',
    name: 'Claude',
    type: 'anthropic',
    requiresApiKey: true,
    defaultBaseUrl: 'https://api.anthropic.com/v1',
    icon: '/logos/claude.svg',
    models: [
      {
        id: 'claude-opus-4-8',
        name: 'Claude Opus 4.8',
        contextWindow: 1000000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'claude-opus-4-7',
        name: 'Claude Opus 4.7',
        contextWindow: 1000000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'claude-opus-4-6',
        name: 'Claude Opus 4.6',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'claude-sonnet-4-6',
        name: 'Claude Sonnet 4.6',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'claude-sonnet-4-5',
        name: 'Claude Sonnet 4.5',
        contextWindow: 200000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'claude-haiku-4-5',
        name: 'Claude Haiku 4.5',
        contextWindow: 200000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
    ],
  },

  google: {
    id: 'google',
    name: 'Gemini',
    type: 'google',
    requiresApiKey: true,
    defaultBaseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    icon: '/logos/gemini.svg',
    models: [
      {
        id: 'gemini-3.5-flash',
        name: 'Gemini 3.5 Flash',
        contextWindow: 1048576,
        outputWindow: 65536,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'gemini-3.1-pro-preview',
        name: 'Gemini 3.1 Pro Preview',
        contextWindow: 1048576,
        outputWindow: 65536,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'gemini-3-flash-preview',
        name: 'Gemini 3 Flash Preview',
        contextWindow: 1048576,
        outputWindow: 65536,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'gemini-2.5-flash',
        name: 'Gemini 2.5 Flash',
        contextWindow: 1048576,
        outputWindow: 65536,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'gemini-2.5-flash-lite',
        name: 'Gemini 2.5 Flash Lite',
        contextWindow: 1048576,
        outputWindow: 65536,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'gemini-2.5-pro',
        name: 'Gemini 2.5 Pro',
        contextWindow: 1048576,
        outputWindow: 65536,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
    ],
  },

  glm: {
    id: 'glm',
    name: 'GLM',
    type: 'openai',
    defaultBaseUrl: 'https://open.bigmodel.cn/api/paas/v4',
    alternateBaseUrls: [
      { label: 'settings.baseUrlRegion.china', url: 'https://open.bigmodel.cn/api/paas/v4' },
      { label: 'settings.baseUrlRegion.international', url: 'https://api.z.ai/api/paas/v4' },
    ],
    requiresApiKey: true,
    icon: '/logos/glm.svg',
    models: [
      // GLM-5.2 Series - Long-horizon coding model (B 3 v7.7.6)
      {
        id: 'glm-5.2',
        name: 'GLM-5.2',
        contextWindow: 1000000,
        outputWindow: 128000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      // GLM-5.1 Series - Latest flagship model
      {
        id: 'glm-5.1',
        name: 'GLM-5.1',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'glm-5v-turbo',
        name: 'GLM-5V-Turbo',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      // GLM-5 Series
      {
        id: 'glm-5',
        name: 'GLM-5',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      // GLM-4.7 Series
      {
        id: 'glm-4.7',
        name: 'GLM-4.7',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'glm-4.7-flashx',
        name: 'GLM-4.7-FlashX',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'glm-4.7-flash',
        name: 'GLM-4.7-Flash',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      // GLM-4.6 Series - Advanced coding & reasoning
      {
        id: 'glm-4.6',
        name: 'GLM-4.6',
        contextWindow: 200000,
        outputWindow: 128000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'glm-4.6v',
        name: 'GLM-4.6V',
        contextWindow: 128000,
        outputWindow: 32000,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'glm-4.6v-flash',
        name: 'GLM-4.6V-Flash',
        contextWindow: 128000,
        outputWindow: 32000,
        capabilities: { streaming: true, tools: true, vision: true },
      },
    ],
  },

  qwen: {
    id: 'qwen',
    name: 'Qwen',
    type: 'openai',
    defaultBaseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    requiresApiKey: true,
    icon: '/logos/qwen.svg',
    models: [
      {
        id: 'qwen3.7-plus',
        name: 'Qwen3.7 Plus',
        contextWindow: 1000000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'qwen3.7-max',
        name: 'Qwen3.7 Max',
        contextWindow: 1000000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'qwen3.6-max-preview',
        name: 'Qwen3.6 Max Preview',
        contextWindow: 256000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'qwen3.6-plus',
        name: 'Qwen3.6 Plus',
        contextWindow: 1000000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'qwen3.6-plus-2026-04-02',
        name: 'Qwen3.6 Plus (2026-04-02)',
        contextWindow: 1000000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'qwen3.6-flash',
        name: 'Qwen3.6 Flash',
        contextWindow: 1000000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'qwen3.6-flash-2026-04-16',
        name: 'Qwen3.6 Flash (2026-04-16)',
        contextWindow: 1000000,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'qwen3.6-35b-a3b',
        name: 'Qwen3.6 35B A3B',
        contextWindow: 262144,
        outputWindow: 64000,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: false,
          },
        },
      },
      {
        id: 'qwen3.5-flash',
        name: 'Qwen3.5 Flash',
        contextWindow: 1000000,
        outputWindow: 65536,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'qwen3.5-plus',
        name: 'Qwen3.5 Plus',
        contextWindow: 1000000,
        outputWindow: 65536,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'qwen3-max',
        name: 'Qwen3 Max',
        contextWindow: 262144,
        outputWindow: 65536,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'qwen3-vl-plus',
        name: 'Qwen3 VL Plus',
        contextWindow: 262144,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: true },
      },
    ],
  },

  deepseek: {
    id: 'deepseek',
    name: 'DeepSeek',
    type: 'openai',
    defaultBaseUrl: 'https://api.deepseek.com/v1',
    requiresApiKey: true,
    icon: '/logos/deepseek.svg',
    models: [
      {
        id: 'deepseek-v4-pro',
        name: 'DeepSeek V4 Pro',
        contextWindow: 1048576,
        outputWindow: 393216,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'deepseek-v4-flash',
        name: 'DeepSeek V4 Flash',
        contextWindow: 1048576,
        outputWindow: 393216,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: true,
            defaultEnabled: true,
          },
        },
      },
    ],
  },

  kimi: {
    id: 'kimi',
    name: 'Kimi',
    type: 'openai',
    defaultBaseUrl: 'https://api.moonshot.cn/v1',
    alternateBaseUrls: [
      { label: 'settings.baseUrlRegion.china', url: 'https://api.moonshot.cn/v1' },
      { label: 'settings.baseUrlRegion.international', url: 'https://api.moonshot.ai/v1' },
    ],
    requiresApiKey: true,
    icon: '/logos/kimi.png',
    models: [
      {
        id: 'kimi-k2.7-code',
        name: 'Kimi K2.7 Code',
        contextWindow: 256000,
        outputWindow: 32768,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'kimi-k2.7-code-highspeed',
        name: 'Kimi K2.7 Code HighSpeed',
        contextWindow: 256000,
        outputWindow: 32768,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'kimi-k2.6',
        name: 'Kimi K2.6',
        contextWindow: 256000,
        outputWindow: 8192,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      // K2.5 Series (2026) - 1T MoE, 32B active parameters
      {
        id: 'kimi-k2.5',
        name: 'Kimi K2.5',
        contextWindow: 256000,
        outputWindow: 8192,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'kimi-k2-thinking',
        name: 'Kimi K2 Thinking',
        contextWindow: 256000,
        outputWindow: 8192,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
    ],
  },

  lemonade: {
    id: 'lemonade',
    name: 'Lemonade',
    type: 'openai',
    defaultBaseUrl: 'http://localhost:13305/v1',
    requiresApiKey: false,
    icon: '/logos/lemonade.svg',
    models: [
      // 【B 3 v7.7.6】启用了之前注释掉的 4 个 lemonade 模型
      // 本地 GGUF 模型推理 (OpenAI-compatible)
      // 见 model-metadata.ts THINKING_CAPABILITIES 中对应条目
      { id: 'Qwen3-4B-GGUF', name: 'Qwen3 4B (GGUF)', contextWindow: 32768, outputWindow: 8192, capabilities: { streaming: true, tools: false, vision: false } },
      { id: 'Qwen3.5-4B-GGUF', name: 'Qwen3.5 4B (GGUF)', contextWindow: 32768, outputWindow: 8192, capabilities: { streaming: true, tools: false, vision: false } },
      { id: 'Gemma-4-26B-A4B-it-GGUF', name: 'Gemma 4 26B A4B IT (GGUF)', contextWindow: 32768, outputWindow: 8192, capabilities: { streaming: true, tools: false, vision: false } },
      { id: 'gpt-oss-20b', name: 'GPT-OSS 20B', contextWindow: 32768, outputWindow: 8192, capabilities: { streaming: true, tools: false, vision: false } },
      { id: 'GPT-OSS-20B-GGUF', name: 'GPT-OSS 20B (GGUF)', contextWindow: 32768, outputWindow: 8192, capabilities: { streaming: true, tools: false, vision: false } },
    ],
  },

    // 【2026-07-02 P0 FIX v5】minimax baseUrl 错 - upstream 默认 /anthropic/v1 (Anthropic-compatible)
    //   但 OpenAI SDK 调用拼成 /anthropic/v1/chat/completions → minimax 没这个 endpoint → 404
    //   DB fa_dongniai_provider_config 用的是 /v1 (OpenAI-compatible) - outline 阶段走自定义
    //   PRIORITY-FALLBACK 显式用 cfg.baseUrl (/v1) 所以成功; scene-content 走标准 resolveModel
    //   fallback 到 defaultBaseUrl (/anthropic/v1) 所以 404
    //   修: defaultBaseUrl 改成 /v1 对齐 DB 配置 (outline 阶段已经验证 /v1 + minimax key 有效)
    minimax: {
    id: 'minimax',
    name: 'MiniMax',
    type: 'openai',
    defaultBaseUrl: 'https://api.minimaxi.com/v1',
    alternateBaseUrls: [
      { label: 'settings.baseUrlRegion.china', url: 'https://api.minimaxi.com/v1' },
      { label: 'settings.baseUrlRegion.international', url: 'https://api.minimax.io/v1' },
    ],
    requiresApiKey: true,
    icon: '/logos/minimax.svg',
    models: [
      {
        id: 'MiniMax-M3',
        name: 'MiniMax M3',
        contextWindow: 204800,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: true, vision: false },
      },
    ],
  },

  siliconflow: {
    id: 'siliconflow',
    name: '硅基流动',
    type: 'openai',
    defaultBaseUrl: 'https://api.siliconflow.cn/v1',
    requiresApiKey: true,
    icon: '/logos/siliconflow.svg',
    models: [
      // DeepSeek Series
      {
        id: 'deepseek-ai/DeepSeek-V3.2',
        name: 'DeepSeek-V3.2',
        contextWindow: 128000,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'deepseek-ai/DeepSeek-R1',
        name: 'DeepSeek-R1',
        contextWindow: 128000,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',
        name: 'DeepSeek-R1-Distill-Qwen-7B',
        contextWindow: 128000,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      // Qwen Series
      {
        id: 'Qwen/Qwen3-VL-32B-Instruct',
        name: 'Qwen3-VL-32B-Instruct',
        contextWindow: 256000,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      // Kimi Series
      {
        id: 'Pro/moonshotai/Kimi-K2.5',
        name: 'Kimi-K2.5',
        contextWindow: 256000,
        outputWindow: 96000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      // GLM Series
      {
        id: 'THUDM/GLM-4.1V-9B-Thinking',
        name: 'GLM-4.1V-9B-Thinking',
        contextWindow: 64000,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'THUDM/GLM-Z1-Rumination-32B-0414',
        name: 'GLM-Z1-Rumination-32B',
        contextWindow: 32000,
        outputWindow: 16384,
        capabilities: { streaming: true, tools: true, vision: false },
      },
    ],
  },

  doubao: {
    id: 'doubao',
    name: '豆包',
    type: 'openai',
    defaultBaseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
    requiresApiKey: true,
    icon: '/logos/doubao.svg',
    models: [
      {
        id: 'doubao-seed-2-0-pro-260215',
        name: 'Doubao Seed 2.0 Pro',
        contextWindow: 128000,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'doubao-seed-2-0-lite-260215',
        name: 'Doubao Seed 2.0 Lite',
        contextWindow: 128000,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'doubao-seed-2-0-mini-260215',
        name: 'Doubao Seed 2.0 Mini',
        contextWindow: 128000,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'doubao-seed-1-8-251228',
        name: 'Doubao Seed 1.8',
        contextWindow: 128000,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: true },
      },
    ],
  },

  openrouter: {
    id: 'openrouter',
    name: 'OpenRouter',
    type: 'openai',
    defaultBaseUrl: 'https://openrouter.ai/api/v1',
    requiresApiKey: true,
    icon: '/logos/openrouter.svg',
    models: [
      {
        id: 'deepseek/deepseek-v4-pro',
        name: 'DeepSeek V4 Pro',
        contextWindow: 1048576,
        outputWindow: 131072,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'deepseek/deepseek-v4-flash',
        name: 'DeepSeek V4 Flash',
        contextWindow: 1048576,
        outputWindow: 131072,
        capabilities: { streaming: true, tools: true, vision: false },
      },
    ],
  },

  grok: {
    id: 'grok',
    name: 'Grok',
    type: 'openai',
    defaultBaseUrl: 'https://api.x.ai/v1',
    requiresApiKey: true,
    icon: '/logos/grok.svg',
    models: [
      {
        id: 'grok-4.20-reasoning',
        name: 'Grok 4.20 Reasoning',
        contextWindow: 2000000,
        outputWindow: 131072,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'grok-4.20',
        name: 'Grok 4.20',
        contextWindow: 2000000,
        outputWindow: 131072,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'grok-4.20-multi-agent',
        name: 'Grok 4.20 Multi-Agent',
        contextWindow: 2000000,
        outputWindow: 131072,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'grok-4-1-fast-reasoning',
        name: 'Grok 4.1 Fast Reasoning',
        contextWindow: 2000000,
        outputWindow: 131072,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: false,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'grok-4-1-fast-non-reasoning',
        name: 'Grok 4.1 Fast',
        contextWindow: 2000000,
        outputWindow: 131072,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'grok-code-fast-1',
        name: 'Grok Code Fast',
        contextWindow: 256000,
        outputWindow: 32768,
        capabilities: { streaming: true, tools: true, vision: false },
      },
    ],
  },

  'tencent-hunyuan': {
    id: 'tencent-hunyuan',
    name: 'Tencent Hunyuan',
    type: 'openai',
    defaultBaseUrl: 'https://tokenhub.tencentmaas.com/v1',
    alternateBaseUrls: [
      { label: 'settings.baseUrlRegion.china', url: 'https://tokenhub.tencentmaas.com/v1' },
      {
        label: 'settings.baseUrlRegion.international',
        url: 'https://tokenhub-intl.tencentmaas.com/v1',
      },
    ],
    requiresApiKey: true,
    icon: '/logos/hunyuan.svg',
    models: [
      {
        id: 'hy3-preview',
        name: 'Tencent Hy3 Preview',
        contextWindow: 256000,
        outputWindow: 64000,
        capabilities: { streaming: true, tools: true, vision: false },
      },
    ],
  },

  xiaomi: {
    id: 'xiaomi',
    name: 'Xiaomi MiMo',
    type: 'openai',
    defaultBaseUrl: 'https://token-plan-cn.xiaomimimo.com/v1',
    requiresApiKey: true,
    icon: '/logos/xiaomi.svg',
    models: [
      {
        id: 'mimo-v2.5-pro',
        name: 'MiMo V2.5 Pro',
        contextWindow: 1048576,
        outputWindow: 131072,
        capabilities: {
          streaming: true,
          tools: true,
          vision: false,
          thinking: {
            toggleable: true,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
      {
        id: 'mimo-v2.5',
        name: 'MiMo V2.5',
        contextWindow: 1048576,
        outputWindow: 131072,
        capabilities: {
          streaming: true,
          tools: true,
          vision: true,
          thinking: {
            toggleable: true,
            budgetAdjustable: false,
            defaultEnabled: true,
          },
        },
      },
    ],
  },

  // 【2026-06-08】DB fa_dongniai_provider_config.id=174 用 provider='xiaomimimo' 配置
  //   - 注册 provider 实体（让 getProviderConfig 能查到）
  //   - **不填 models 数组** —— 严格按老板规则："DB model_id 填什么就调什么"
  //     真实调用的 modelId 由 DB.fa_dongniai_provider_config.model_id 字段决定
  //     前端绝不擅自加任何 model（避免有人手抖配了 mimo-v2.5，前端却调成 mimo-v2.5-pro）
  //   - getModel() line 1411: provider?.models.find(...) 找不到就 null，但 model 仍按 config.modelId 调用
  xiaomimimo: {
    id: 'xiaomimimo',
    name: 'Xiaomi MiMo',
    type: 'openai',
    defaultBaseUrl: 'https://token-plan-cn.xiaomimimo.com/v1',
    requiresApiKey: true,
    icon: '/logos/xiaomi.svg',
    models: [], // 严格按 DB model_id 字段调用，前端不预设任何模型
  },

  ollama: {
    id: 'ollama',
    name: 'Ollama',
    type: 'openai',
    defaultBaseUrl: 'http://localhost:11434/v1',
    requiresApiKey: false,
    icon: '/logos/ollama.svg',
    models: [
      {
        id: 'llama3.3',
        name: 'Llama 3.3 70B',
        contextWindow: 131072,
        outputWindow: 4096,
        capabilities: { streaming: true, tools: true, vision: false },
      },
      {
        id: 'gemma3',
        name: 'Gemma 3 12B',
        contextWindow: 131072,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: true, vision: true },
      },
      {
        id: 'deepseek-r1',
        name: 'DeepSeek R1',
        contextWindow: 131072,
        outputWindow: 8192,
        capabilities: { streaming: true, tools: false, vision: false },
      },
    ],
  },
};

applyModelMetadata(PROVIDERS);

/**
 * Get provider config (from built-in or unified config in localStorage)
 */
function getProviderConfig(providerId: ProviderId): ProviderConfig | null {
  // Check built-in providers first
  if (PROVIDERS[providerId]) {
    return PROVIDERS[providerId];
  }

  // Check unified providersConfig in localStorage (browser only)
  if (typeof window !== 'undefined') {
    try {
      const storedConfig = localStorage.getItem('providersConfig');
      if (storedConfig) {
        const config = JSON.parse(storedConfig);
        const providerSettings = config[providerId];
        if (providerSettings) {
          return {
            id: providerId,
            name: providerSettings.name,
            type: providerSettings.type,
            defaultBaseUrl: providerSettings.defaultBaseUrl,
            icon: providerSettings.icon,
            requiresApiKey: providerSettings.requiresApiKey,
            models: providerSettings.models,
          };
        }
      }
    } catch (e) {
      log.error('Failed to load provider config:', e);
    }
  }

  return null;
}

/**
 * Model instance with its configuration info
 */
export interface ModelWithInfo {
  model: LanguageModel;
  modelInfo: ModelInfo | null;
}

function getCompatThinkingBodyParams(
  providerId: ProviderId,
  modelId: string,
  config: ThinkingConfig,
): Record<string, unknown> | undefined {
  const capability = getCatalogThinkingCapability(providerId, modelId);
  if (!capability || capability.control === 'none') return undefined;

  // 关键修复：config=undefined 时 getThinkingMode 返回 undefined，
  // 会导致 minimax 等 model 默认 thinking ON（"no param = thinking ON"），卡 60s+ 后 502。
  // fallback 到 capability.defaultMode（从 model-metadata.ts 来的 metadata）。
  // 这与 model-metadata.ts 中 toggleCapability('minimax', false) 配合：
  //   metadata defaultMode='disabled' → fallback mode='disabled' → 显式传 {thinking: {type: 'disabled'}} 走 fast path
  const rawMode = getThinkingMode(config);
  const mode =
    rawMode ?? capability.defaultMode ?? (capability.defaultEnabled === false ? 'disabled' : 'enabled');
  const budget = pickThinkingBudget(capability, config);

  switch (capability.requestAdapter) {
    case 'kimi':
    case 'glm':
    case 'xiaomi':
      if (mode === 'disabled') return { thinking: { type: 'disabled' } };
      if (mode === 'enabled') return { thinking: { type: 'enabled' } };
      return undefined;

    case 'minimax':
    case 'anthropic':
      // MiniMax-M3 (and similar MiniMax reasoning models) special handling:
      // All thinking modes empirically produce empty text from MiniMax's API
      // EXCEPT { thinking: { type: 'disabled' } }, which returns valid JSON reliably.
      // Therefore we always return disabled, regardless of mode:
      //   - "default"  → always disabled (the only stable mode)
      //   - "disabled" → always disabled
      //   - "enabled"  → also disabled (admin chose "enabled" but MiniMax's "enabled"
      //     mode returns empty — this is a documented limitation of MiniMax-M3)
      // The admin's thinking setting still affects other providers' thinking behavior.
      //
      // 【2026-07-02 P0 FIX v6】metadata 里 MiniMax-M3 用 'anthropic' requestAdapter
      //   (dongniai adapter 是 anthropic-style thinking), 但 switch 里没 case 'anthropic'
      //   → thinking body 永远 undefined → minimax 默认 thinking ON → 返回 think 块
      //   → strip 后空 → Failed to parse JSON → scene-content 全失败
      //   修: case 'anthropic' 跟 case 'minimax' 一样强制 disabled
      return { thinking: { type: 'disabled' } };

    case 'deepseek': {
      if (mode === 'disabled' || config.effort === 'none') {
        return { thinking: { type: 'disabled' } };
      }

      const effort = config.effort === 'max' || config.effort === 'xhigh' ? 'max' : 'high';
      return {
        thinking: { type: 'enabled' },
        reasoning_effort: effort,
      };
    }

    case 'qwen': {
      if (mode === 'disabled') return { enable_thinking: false };
      const body: Record<string, unknown> = {};
      if (mode === 'enabled') body.enable_thinking = true;
      if (budget !== undefined) body.thinking_budget = budget;
      return Object.keys(body).length > 0 ? body : undefined;
    }

    case 'siliconflow': {
      const body: Record<string, unknown> = {};
      if (capability.control === 'toggle-budget') {
        if (mode === 'disabled') body.enable_thinking = false;
        if (mode === 'enabled') body.enable_thinking = true;
      }
      if (budget !== undefined) body.thinking_budget = budget;
      return Object.keys(body).length > 0 ? body : undefined;
    }

    // 【v6.97.125 2026-09-12 P0 修复】中文名 provider (智谱清言/通义千问/百炼 等) 不进注册表
    //   → THINKING_CAPABILITIES 查不到 → getCatalogThinkingCapability 走兜底返回 toggleCapability('openai-compat', false)
    //   → 这里 'openai-compat' case 显式 return {thinking:{type:'disabled'}} (mode==='disabled')
    //   修法: 加 'openai-compat' case 处理所有未注册的中文名 provider
    case 'openai-compat': {
      // 所有未识别 OpenAI 兼容 provider 默认 disabled thinking — reasoning 是少数
      if (mode === 'disabled') return { thinking: { type: 'disabled' } };
      if (mode === 'enabled') return { thinking: { type: 'enabled' } };
      return undefined;
    }

    case 'doubao': {
      if (capability.control === 'effort') {
        const effort =
          mode === 'disabled'
            ? 'minimal'
            : config.effort && capability.effortValues?.includes(config.effort)
              ? config.effort
              : mode === 'enabled'
                ? capability.defaultEffort
                : undefined;
        return effort ? { reasoning_effort: effort } : undefined;
      }
      if (mode === 'auto') return { thinking: { type: 'auto' } };
      if (mode === 'disabled') return { thinking: { type: 'disabled' } };
      if (mode === 'enabled') return { thinking: { type: 'enabled' } };
      return undefined;
    }

    case 'openrouter': {
      const reasoning: Record<string, unknown> = {};
      if (mode === 'disabled') reasoning.enabled = false;
      if (mode === 'enabled') reasoning.enabled = true;
      if (config.effort) reasoning.effort = config.effort;
      if (budget !== undefined) reasoning.max_tokens = budget;
      if (typeof config.excludeReasoningOutput === 'boolean') {
        reasoning.exclude = config.excludeReasoningOutput;
      }
      return Object.keys(reasoning).length > 0 ? { reasoning } : undefined;
    }

    case 'hunyuan': {
      let reasoningEffort: 'no_think' | 'low' | 'high' | undefined;
      if (mode === 'disabled' || config.effort === 'none') {
        reasoningEffort = 'no_think';
      } else if (config.effort === 'high' || config.effort === 'max' || config.effort === 'xhigh') {
        reasoningEffort = 'high';
      } else if (
        config.effort === 'low' ||
        config.effort === 'medium' ||
        config.effort === 'minimal'
      ) {
        reasoningEffort = 'low';
      } else if (mode === 'enabled') {
        reasoningEffort = capability.defaultEffort === 'high' ? 'high' : 'low';
      }
      return reasoningEffort
        ? { chat_template_kwargs: { reasoning_effort: reasoningEffort } }
        : undefined;
    }

    default:
      return undefined;
  }
}

function normalizeMiniMaxAnthropicBaseUrl(
  providerId: ProviderId,
  baseUrl?: string,
): string | undefined {
  if (providerId !== 'minimax' || !baseUrl) {
    return baseUrl;
  }

  const trimmed = baseUrl.replace(/\/$/, '');
  // MiniMax OpenAI-compatible endpoint is /v1 (not /anthropic/v1)
  if (trimmed.endsWith('/v1')) {
    return trimmed;
  }
  if (trimmed.endsWith('/anthropic/v1')) {
    return trimmed;
  }
  if (trimmed.endsWith('/anthropic')) {
    return `${trimmed}/v1`;
  }
  return `${trimmed}/v1`;
}

function shouldUseOpenAIResponsesApi(providerId: ProviderId, modelId: string): boolean {
  if (providerId !== 'openai') return false;

  return (
    /^gpt-5\.\d+-pro(?:-|$)/.test(modelId) ||
    /^gpt-5\.5(?:-|$)/.test(modelId) ||
    /^gpt-5\.[3-9]-codex(?:-|$)/.test(modelId)
  );
}

/** Returns true if the provider requires an API key (defaults to true for unknown providers). */
export function isProviderKeyRequired(providerId: string): boolean {
  return getProviderConfig(providerId as ProviderId)?.requiresApiKey ?? true;
}

/**
 * Get a configured language model instance with its info
 * Accepts individual parameters for flexibility and security
 */
export function getModel(config: ModelConfig): ModelWithInfo {
  // providerType can come from client for custom providers; fall back to registry.
  let providerType = config.providerType;
  const provider = getProviderConfig(config.providerId);
  const requiresApiKey = provider?.requiresApiKey ?? true;

  if (!providerType) {
    if (provider) {
      providerType = provider.type;
    } else if (config.baseUrl) {
      // 【v6.97.122 2026-09-12】自定义 provider（后台配置的中文名，如"智谱清言"）不进注册表:
      //   modelId / baseUrl / apiKey 全部由 caller 显式传入 (cfg.model_id / cfg.base_url / cfg.api_key),
      //   协议按 OpenAI 兼容处理 (智谱/百炼/月之暗面等主流 provider 均兼容)。
      //   已注册 provider (minimax/deepseek/...) 走上面的注册表 type, 不受影响。
      providerType = 'openai';
    } else {
      throw new Error(`Unknown provider: ${config.providerId}. Please provide providerType.`);
    }
  }

  // Validate API key if required
  if (requiresApiKey && !config.apiKey) {
    throw new Error(`API key required for provider: ${config.providerId}`);
  }

  // 【2026-09-12 v6.97.124 P0 防线】任何 masked/PHP-mask 字符串进 getModel 立刻 throw,
  //   防止 fetch wrapper 降级循环里有人错传 '***XXXX' / 'sk-***-1k2Y' 等假 key,
  //   导致 provider 返回 1004/401/429 后被误诊为 quota 问题.
  // 4 重检测 (与 key-access.ts isRealApiKey 对齐):
  //   1. length >= 16      (PHP mask '***XXXX' 仅 9 字符, 真 key 普遍 30+)
  //   2. !includes('*')    (mask 中段必有星号)
  //   3. !== '***'         (空 mask 占位)
  //   4. !== 'PLACEHOLDER' (测试占位符)
  // 抛错信息精确: 列出 provider + 假 key 前 8 字符 + 建议 (走 key-access.ts resolveApiKey)
  if (requiresApiKey && config.apiKey) {
    const k = config.apiKey;
    const looksLikeMask =
      k.length < 16 ||
      k.includes('*') ||
      k === '***' ||
      k === 'PLACEHOLDER';
    if (looksLikeMask) {
      throw new Error(
        `[getModel P0 防线] provider=${config.providerId} 收到 masked/fake apiKey ` +
        `(前 8 字符: "${k.slice(0, 8)}", 长度 ${k.length}). ` +
        `这是 PHP 端 mask 字符串, 不能用于调 LLM. ` +
        `请走 '@/lib/server/key-access' 的 resolveApiKey() 或 fetchAllAdminLlmConfigs() ` +
        `(内部已自动调 ?method=getKey 拿真 key, 1 秒缓存).`,
      );
    }
  }

  // Use provided API key, or empty string for providers that don't require one
  const effectiveApiKey = config.apiKey || '';

  // Resolve base URL: explicit > provider default > SDK default
  const effectiveBaseUrl = normalizeMiniMaxAnthropicBaseUrl(
    config.providerId,
    config.baseUrl || provider?.defaultBaseUrl || undefined,
  );

  let model: LanguageModel;

  switch (providerType) {
    case 'openai': {
      const openaiOptions: Parameters<typeof createOpenAI>[0] = {
        apiKey: effectiveApiKey,
        baseURL: effectiveBaseUrl,
      };

      // For OpenAI-compatible providers (not native OpenAI), add a fetch
      // wrapper that injects vendor-specific thinking params into the HTTP
      // body. The thinking config is read from AsyncLocalStorage, set by
      // callLLM / streamLLM at call time.
      if (config.providerId !== 'openai') {
        const providerId = config.providerId;
        // Capture admin thinking override as a closure variable (NOT from ALS).
        // ALS does NOT propagate reliably through the AI SDK 5 / undici fetch wrapper
        // (undici runs in a separate async context that doesn't see enterWith values).
        // The override is passed in via the optional __dongniaiAdminOverride symbol on
        // config — see lib/server/resolve-model.ts.
        const closureAdminOverride = (config as unknown as Record<string, unknown>).__dongniaiAdminOverride as
          | boolean | 'enabled' | 'disabled' | 'default' | undefined;
          // 完整 fallback cfg 列表（priority 升序），用于 fetch 失败时降级到下一级
          const closureFallbackCfgs = (config as unknown as Record<string, unknown>).__dongniaiFallbackCfgs as
            | Array<{ providerId: string; modelId: string; apiKey: string; baseUrl: string; priority: number }>
            | undefined;
          if (closureFallbackCfgs?.length) {
            console.log(`[LLM-FALLBACK-DEBUG] fetch wrapper initialized with ${closureFallbackCfgs.length} fallback cfgs:`, closureFallbackCfgs.map(c => `${c.providerId}/${c.modelId}@p${c.priority}`).join(","));
          }
        openaiOptions.fetch = async (url: RequestInfo | URL, init?: RequestInit) => {
          // 【2026-07-02 P0 FIX v8.1】thinking 注入移到 fetch 之前 (真因修复)
          // 原顺序 (fetch 先, inject 后) bug: 第一次 fetch 用原始 init 发出去,
          //   minimax 默认 thinking=ON → 82s 长思考 → 返回 24k+ 字符纯 think 块或空 content
          //   → strip 后空 → Failed to parse JSON → scene-content / pbl / chat / quiz 全失败
          // outline stage 走 PRIORITY-FALLBACK 路径 (显式 admin override + 改 body), 所以 outline 成功
          // 修: 先算 effectiveThinking + 注入 thinking:{type:'disabled'} 到 init.body → 才走第一次 fetch
          //     fallback 重试也用 modifiedInit, 确保整个 chain 都有 thinking disabled
          const thinkingCtx = (globalThis as Record<string, unknown>).__thinkingContext as
            | { getStore?: () => unknown }
            | undefined;
          const thinking = thinkingCtx?.getStore?.() as ThinkingConfig | undefined;

          // Closure variable (preferred) → ALS (fallback for other call paths)
          const adminCtx = (globalThis as Record<string, unknown>).__adminThinkingContext as
            | { getStore?: () => unknown }
            | undefined;
          const alsAdmin = adminCtx?.getStore?.() as boolean | 'enabled' | 'disabled' | 'default' | undefined;
          const rawAdminOverride = closureAdminOverride ?? alsAdmin;

          let effectiveThinking = thinking;
          if (rawAdminOverride === true || rawAdminOverride === 'enabled') {
            effectiveThinking = { mode: 'enabled' };
          } else if (rawAdminOverride === false || rawAdminOverride === 'disabled') {
            effectiveThinking = { mode: 'disabled' };
          } else if (rawAdminOverride === 'default') {
            // admin=default 时让模型用默认行为；minimax-M3 默认 thinking=ON 会导致
            // 24k+ 字符纯 think block + 0 JSON 失败，所以对 minimax 强制 disabled。
            effectiveThinking = providerId === 'minimax' ? { mode: 'disabled' } : undefined;
          }
          // 【2026-07-02 P0 FIX v7】minimax 总是强制 disabled (兜底) — 不依赖 admin override
          // 【v6.97.138 P0 FIX 2026-09-15】v6.97.125 重构后 providerId=model_id ('MiniMax-M3')，
          //   providerId==='minimax' 恒 false → 兜底失效 → effectiveThinking=undefined → SKIP 注入
          //   → MiniMax API 默认思考 ON → 单 call 90~240s（8 月同模型 40s 的回归根因）。
          //   改为 MiniMax 家族识别 (providerId 或 modelId 任一匹配)。
          const isMiniMaxFamily =
            providerId === 'minimax' ||
            /^MiniMax/i.test(providerId) ||
            /^MiniMax/i.test(String(config?.modelId ?? ''));
          if (isMiniMaxFamily && !effectiveThinking) {
            effectiveThinking = { mode: 'disabled' };
          }

          // PRE-FETCH thinking injection (v8.1)
          let modifiedInit: RequestInit = init ?? {};
          if (effectiveThinking && modifiedInit.body && typeof modifiedInit.body === 'string') {
            const extra = getCompatThinkingBodyParams(providerId, config.modelId, effectiveThinking);
            if (extra) {
              try {
                const body = JSON.parse(modifiedInit.body as string);
                Object.assign(body, extra);
                modifiedInit = { ...modifiedInit, body: JSON.stringify(body) };
                // 【DEBUG v8.1】log pre-fetch injected thinking body
                console.log(`[LLM-THINKING-DEBUG] provider=${providerId} model=${config.modelId} pre-fetch injected:`, JSON.stringify(extra));
              } catch (e) {
                console.error(`[LLM-THINKING-DEBUG] pre-fetch inject failed for ${providerId}:`, e);
              }
            } else {
              console.log(`[LLM-THINKING-DEBUG] provider=${providerId} model=${config.modelId} getCompatThinkingBodyParams returned undefined (effectiveThinking=${JSON.stringify(effectiveThinking)})`);
            }
          } else {
            console.log(`[LLM-THINKING-DEBUG] provider=${providerId} model=${config.modelId} SKIP pre-fetch inject (effectiveThinking=${JSON.stringify(effectiveThinking)}, body type=${typeof modifiedInit.body})`);
          }

          // 【v0.3.0 兼容 dongniai】First fetch attempt (now with thinking injected); subsequent retries fall back to lower-priority cfgs.
          const firstResp = await globalThis.fetch(url, modifiedInit);
          // === 优先级降级逻辑（循环所有 fallback configs） ===
          // 如果返回 401/402/403/429/5xx → 按 priority ASC 依次尝试所有 cfg，直到成功或全部失败
          if (closureFallbackCfgs && closureFallbackCfgs.length > 1) {
            let lastStatus = firstResp.status;
            const isTransientError = (s: number) => s === 400 || s === 401 || s === 402 || s === 403 || s === 429 || (s >= 500 && s < 600);
            if (isTransientError(lastStatus)) {
              let currentPriority = 0;
              try {
                const bodyObj = modifiedInit.body ? JSON.parse(String(modifiedInit.body)) : null;
                const curModelId = bodyObj?.model || config.modelId;
                const cur = closureFallbackCfgs.find(c => c.modelId === curModelId);
                currentPriority = cur?.priority ?? 0;
              } catch { /* keep 0 */ }

              const remainingCfgs = closureFallbackCfgs
                .filter(c => c.priority > currentPriority)
                .sort((a, b) => a.priority - b.priority);

              try { await firstResp.text(); } catch { /* ignore */ }

              let lastResp = firstResp;
              for (const nextCfg of remainingCfgs) {
                console.warn(
                  `[LLM-FALLBACK] ${providerId}/${config.modelId} (priority=${currentPriority}) ` +
                  `returned HTTP ${lastStatus}; trying ${nextCfg.providerId}/${nextCfg.modelId} (priority=${nextCfg.priority})`
                );

                let newUrl = url;
                let newInit: RequestInit = modifiedInit ? { ...modifiedInit, headers: modifiedInit.headers ? { ...(modifiedInit.headers as Record<string, string>) } : {} } : {};
                if (nextCfg.baseUrl && typeof url === 'string') {
                  try {
                    const u = new URL(url);
                    const base = new URL(nextCfg.baseUrl);
                    u.protocol = base.protocol;
                    u.host = base.host;
                    newUrl = u.toString();
                  } catch { /* keep original url */ }
                }
                const headers = newInit.headers as Record<string, string>;
                if (nextCfg.apiKey) {
                  headers['Authorization'] = `Bearer ${nextCfg.apiKey}`;
                  headers['x-api-key'] = nextCfg.apiKey;
                }
                if (newInit.body && typeof newInit.body === 'string') {
                  try {
                    const bodyObj = JSON.parse(newInit.body);
                    bodyObj.model = nextCfg.modelId;
                    newInit.body = JSON.stringify(bodyObj);
                  } catch { /* keep body */ }
                }

                const retryResp = await globalThis.fetch(newUrl, newInit);
                lastStatus = retryResp.status;
                lastResp = retryResp;

                if (!isTransientError(lastStatus)) {
                  return retryResp;
                }

                console.warn(
                  `[LLM-FALLBACK] ${nextCfg.providerId}/${nextCfg.modelId} (priority=${nextCfg.priority}) ` +
                  `also returned HTTP ${lastStatus}; trying next...`
                );
                try { await retryResp.text(); } catch { /* ignore */ }
              }

              return lastResp;
            }
          }
          return firstResp;
        };
      }

      const openai = createOpenAI(openaiOptions);
      model = shouldUseOpenAIResponsesApi(config.providerId, config.modelId)
        ? openai.responses(config.modelId)
        : openai.chat(config.modelId);
      break;
    }

    case 'anthropic': {
      const anthropic = createAnthropic({
        apiKey: effectiveApiKey,
        baseURL: effectiveBaseUrl,
      });
      model = anthropic.chat(config.modelId);
      break;
    }

    case 'google': {
      const googleOptions: Parameters<typeof createGoogleGenerativeAI>[0] = {
        apiKey: effectiveApiKey,
        baseURL: effectiveBaseUrl,
      };
      if (config.proxy) {
        const proxy = config.proxy;
        let agent: unknown;
        googleOptions.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
          const { ProxyAgent, fetch: undiciFetch } = (await import(
            /* webpackIgnore: true */ 'undici'
          )) as {
            ProxyAgent: new (proxyUrl: string) => unknown;
            fetch: (
              input: string | URL | Request,
              init?: Record<string, unknown>,
            ) => Promise<unknown>;
          };
          agent ??= new ProxyAgent(proxy);
          const response = await undiciFetch(input, {
            ...(init as Record<string, unknown>),
            dispatcher: agent,
          });
          return response as Response;
        }) as typeof fetch;
      }
      const google = createGoogleGenerativeAI(googleOptions);
      model = google.chat(config.modelId);
      break;
    }

    default:
      throw new Error(`Unsupported provider type: ${providerType}`);
  }

  // Look up model info from the provider registry
  const modelInfo = provider?.models.find((m) => m.id === config.modelId) || null;

  // 【2026-09-12 v6.97.125 P0 防线】getModel 返回前必须 model 非 undefined
  // 真实背景: exercise/start E2E 触发 "Cannot read properties of undefined (reading 'specificationVersion')"
  //   - 老板 0.27s 同步抛, 怀疑 v6.97.122 providerType 透传路径某处把 model 弄丢了
  //   - 真凶待查 (这条防线先加, 抛明确错避免 30s nginx 504 误导排错)
  if (!model) {
    throw new Error(
      `[getModel P0 防线] provider=${config.providerId} modelId=${config.modelId} ` +
      `providerType=${providerType} 返回 model=undefined. ` +
      `这是 v6.97.122 修复 chain 某处的隐藏 bug, 请立刻报老板.`,
    );
  }
  console.log(`[getModel-DEBUG] provider=${config.providerId} modelId=${config.modelId} providerType=${providerType} model.specificationVersion=${model.specificationVersion} modelId=${model.modelId}`);
  return { model, modelInfo };
}

/**
 * Parse model string in format "providerId:modelId" or just "modelId" (defaults to OpenAI)
 */
export function parseModelString(modelString: string): {
  providerId: ProviderId;
  modelId: string;
} {
  // Split only on the first colon to handle model IDs that contain colons
  const colonIndex = modelString.indexOf(':');

  if (colonIndex > 0) {
    return {
      providerId: modelString.slice(0, colonIndex) as ProviderId,
      modelId: modelString.slice(colonIndex + 1),
    };
  }

  // Default to OpenAI for backward compatibility
  return {
    providerId: 'openai',
    modelId: modelString,
  };
}

/**
 * Get all available models grouped by provider
 */
export function getAllModels(): {
  provider: ProviderConfig;
  models: ModelInfo[];
}[] {
  return Object.values(PROVIDERS).map((provider) => ({
    provider,
    models: provider.models,
  }));
}

/**
 * Get provider by ID
 */
export function getProvider(providerId: ProviderId): ProviderConfig | undefined {
  return PROVIDERS[providerId];
}

/**
 * Get model info
 */
export function getModelInfo(providerId: ProviderId, modelId: string): ModelInfo | undefined {
  const provider = PROVIDERS[providerId];
  return provider?.models.find((m) => m.id === modelId);
}
